import { forwardRef, useCallback, useEffect, useState } from "react";
import { getExperienceList } from "../../../../endpoints/experience.service";
import { useDispatch, useSelector } from "react-redux";
import { setUserData } from "../../../../store/actions/auth.action";
import { ExperienceData, ExperienceProps } from "./experience.types";
import ExperienceBlock from "./experienceBlock/experienceBlock";
import PlusIcon from "../../../../assets/svg/PlusIcon";
import { isNullOrEmpty } from "../../../../utils/helperFunction";
import LoadingAnimation from "../../../../assets/svg/LoadingAnimation";
import { colors } from "../../../../utils/theme";
import { ClipboardList, CodeXml, Medal, Minus, Plus, ScrollText } from "lucide-react";

const experiencesDummyData = [
  {
    jobTitle: "Senior Full Stack Developer",
    companyName: "Technova Solutions Ltd",
    location: "Manchester, United Kingdom",
    summary: "Proficiency in HTML/CSS, JavaScript, server languages (Node.js, Python), database management (SQL/NoSQL),and API integration, aimed at managing the entire web application development lifecycle.",
    keyResponsibilities: "Key Responsibilities",
    keyAchievements: "Key Achievements",
    skills: "Skills",
  },
  {
    jobTitle: "Senior Full Stack Developer",
    companyName: "Technova Solutions Ltd",
    location: "Manchester, United Kingdom",
    summary: "Proficiency in HTML/CSS, JavaScript, server languages (Node.js, Python), database management (SQL/NoSQL),and API integration, aimed at managing the entire web application development lifecycle.",
    keyResponsibilities: "Key Responsibilities",
    keyAchievements: "Key Achievements",
    skills: "Skills",
  }
]

const tabs = [
  {
    label: "Summary",
    icon: <ScrollText size={17} />,
    value: 0,
    content: <p>Proficiency in HTML/CSS, JavaScript, server languages (Node.js, Python), database management (SQL/NoSQL),and API integration, aimed at managing the entire web application development lifecycle.</p>
  },
  {
    label: "Key Responsibilities",
    icon: <ClipboardList size={17} />,
    value: 1,
  },
  {
    label: "Key Achievements",
    icon: <Medal size={17} />,
    value: 2,
  },
  {
    label: "Skills",
    icon: <CodeXml size={17} />,
    value: 3,
  },
]

const ExperienceCard = ({
  experience
}) => {
  const [activeTab, setActiveTab] = useState(0)
  const [expandCard, setExpandCard] = useState(false)

  const RenderContent = () => {
    return <div className="px-10 pt-6 pb-12 font-medium leading-5 text-xs text-light-black">
      {tabs?.find((tab) => tab?.value === activeTab)?.content}
    </div>
  }


  return <div className="flex flex-col rounded-xl border bg-white">
    <div className="flex items-center justify-between p-5">
      <div className="flex items-center gap-5">
        {/* Left border */}
        <div className="w-1 h-14 rounded-full bg-slate-700"></div>
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-8">
            <span className="font-bold text-slate-700">{experience?.jobTitle}</span>
            <span className="font-medium text-xs text-light-black">January 2020 - Present</span>
          </div>
          <div className="flex items-center gap-5">
            <span className="font-medium text-xs text-light-black">{experience?.companyName}</span>
            <div className="w-px h-3 bg-light-black" />
            <span className="font-medium text-xs text-light-black">{experience?.location}</span>
          </div>
        </div>
      </div>
      <button
        onClick={() => setExpandCard(!expandCard)}
        aria-expanded={expandCard}
        className="relative rounded-full flex items-center justify-center h-7 w-7 bg-slate-700">
        <Plus
          size={17}
          color="white"
          className={`absolute transition-all duration-300 ease-out ${expandCard ? "opacity-0 rotate-90 scale-50" : "opacity-100 rotate-0 scale-100"}`}
        />
        <Minus
          size={17}
          color="white"
          className={`absolute transition-all duration-300 ease-out ${expandCard ? "opacity-100 rotate-0 scale-100" : "opacity-0 -rotate-90 scale-50"}`}
        />
      </button>
    </div>
    {/* Expanded section */}
    <div
      className={`grid transition-[grid-template-rows] duration-300 ease-out ${expandCard ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
    >
      <div className="overflow-hidden min-h-0">
        <div className="flex flex-col gap-2 border-t">
          {/* Tabs container */}
          <div className="flex items-center px-5 gap-5 border-b-2">
            {
              tabs?.map((tab) => {
                const isActive = tab?.value === activeTab;
                return <button key={tab?.value} onClick={() => setActiveTab(tab?.value)} className={`flex flex-col items-center flex-1`}>
                  <div className="flex items-center py-4 gap-2">
                    <span>{tab?.icon}</span>
                    <span className={`font-semibold text-xs ${isActive ? "text-slate-700" : "text-light-black"}`}>{tab?.label}</span>
                  </div>
                  <div className={`w-full h-1 rounded-full transition-colors duration-300 ${isActive ? "bg-slate-700" : "bg-transparent"}`}></div>
                </button>
              })
            }
          </div>

          {/* Render Content */}
          <RenderContent />
        </div>
      </div>
    </div>
  </div>
}

const Experience2 = forwardRef<HTMLDivElement, ExperienceProps>(
  (
    { isEditing, handleNext, handleEditClose, showProfile, candidateId },
    ref
  ) => {
    const { user } = useSelector((state: any) => state.auth);
    const [experiences, setExperiences] = useState<ExperienceData[]>(
      user?.experience ?? []
    );
    const [isFetching, setIsFetching] = useState<boolean>(
      user?.experience ? true : false
    );
    const [showModal, setShowModal] = useState<boolean>(false);
    const [isAddingNew, setIsAddingNew] = useState<boolean>(false);
    const dispatch = useDispatch();

    useEffect(() => {
      setShowModal(isEditing);
    }, [isEditing]);

    const closeModal = (): void => {
      setShowModal(false);
      setIsAddingNew(false);
      handleEditClose();
    };

    useEffect(() => {
      dispatch(setUserData({ experiences: experiences?.filter((x) => x?._id) }));
    }, [experiences, dispatch]);

    const fetchExperienceList = useCallback(async () => {
      try {
        setIsFetching(true);
        const { success, data } = await getExperienceList({
          candidateId: candidateId,
        });
        if (success) {
          setExperiences(data);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsFetching(false);
      }
    }, [candidateId]);

    useEffect(() => {
      fetchExperienceList();
    }, [fetchExperienceList]);

    // if (isFetching) {
    //   return (
    //     <div className="flex flex-1  flex-col items-center justify-center">
    //       <LoadingAnimation color={colors.PrimaryBlack} />
    //     </div>
    //   );
    // }

    return <div className="flex flex-col gap-3">
      {
        experiencesDummyData?.map((experience) => {
          return <ExperienceCard
            key={experience?.jobTitle}
            experience={experience}
            
          />
        })
      }
    </div>

    return (
      <div ref={ref} className="flex flex-col flex-1 px-6">
        {isNullOrEmpty(experiences) ? (
          <div className="flex flex-1  flex-col items-center justify-center mt-16">
            <p className="text-charcoal font-semibold">
              No Work Experience Created
            </p>
            {!showProfile && (
              <>
                <p className="mt-2 mb-5">Create a Work Experience Now!</p>
                <button
                  onClick={() => {
                    setShowModal(true);
                    setIsAddingNew(true);
                  }}
                  className="flex items-center justify-center border bg-green-600 rounded-md px-2 py-1 gap-1 text-white font-medium text-sm"
                >
                  <PlusIcon className={"w-4 h-4"} fill={"white"} />
                  Add Experience
                </button>
              </>
            )}
          </div>
        ) : (
          experiencesDummyData?.map((experience) => {
            return <ExperienceCard
              key={experience?.jobTitle}
              experience={experience}
            />
          })
          // <ExperienceBlock experiences={experiences} showOnly />
        )}

        {showModal && (
          <div
            className="fixed top-0 left-0 w-full h-full bg-black bg-opacity-50 flex justify-center items-center z-50"
            onClick={closeModal}
          >
            <div
              className="flex overflow-hidden flex-col bg-white rounded-lg relative w-[60%] max-h-[80%] ml-9 max-2xl:w-[70%] max-xl:w-[87%] max-xl:ml-16"
              onClick={(e) => e.stopPropagation()}
            >
              <ExperienceBlock
                popUpShow
                experiences={experiences}
                closeModal={closeModal}
                setExperiences={setExperiences}
                isAddingNew={isAddingNew}
              />
            </div>
          </div>
        )}
      </div>
    );
  }
);

export default Experience2;
